import React, { useEffect, useState } from "react";
import Checkbox from "../Checkbox";
import { CheckedState } from "@radix-ui/react-checkbox";
import { Label } from "../Label";
import Button from "@/components/atoms/Button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import clsx from "clsx";

type ItemProps =
  | {
      access: boolean;
      data: TransferDataType[];
      constSourceData: TransferDataType[];
      items: TransferDataType[];
      selectedKeys: number[];
      setSelectedKeys: (items: number[]) => void;
    }
  | {
      access: boolean;
      data: TransferDataType[];
      constSourceData: TransferDataType[];
      items: TransferDataType[];
      selectedKeys: number[];
      setSelectedKeys: (items: number[]) => void;
    };

type BoxProps = ItemProps & {
  title: string;
};

const TargetBox = ({
  access,
  title,
  data,
  items,
  selectedKeys,
  constSourceData,
  setSelectedKeys,
}: BoxProps) => {
  const listHeaderClasses =
    "px-2 py-1 border border-blue dark:border-border border-b-0 rounded-t bg-blue dark:bg-blue text-white";

  const headerLabelClasses = "flex items-center py-1";

  const [selectAll, setSelectAll] = useState(false);

  const handleSelect = (
    item: TransferDataType,
    checked: CheckedState,
    parent?: TransferDataType
  ) => {
    const isParent = items.find((d) => d.id === item.id);
    let newSelectedKeys = selectedKeys;

    if (checked) {
      if (isParent) {
        newSelectedKeys = [
          ...selectedKeys,
          ...(selectedKeys.includes(item.id) ? [] : [item.id]),
          ...(isParent.children ? isParent.children : [])
            .filter((s) => !selectedKeys.includes(s.id))
            .map((s) => s.id),
        ];
      } else {
        newSelectedKeys = [
          ...selectedKeys,
          ...(selectedKeys.includes(item.id) ? [] : [item.id]),
          ...(parent?.id && !selectedKeys.includes(parent.id)
            ? [parent.id]
            : []),
        ];
      }
    } else {
      setSelectAll(false);

      if (isParent) {
        newSelectedKeys = selectedKeys.filter(
          (i) => i !== item.id && !item.children?.find((s) => i === s.id)
        );
      } else {
        newSelectedKeys = selectedKeys.filter((i) => i !== item.id);
        const parent = items.find(({ children = [] }) =>
          children.find((s) => s.id === item.id)
        );
        if (
          parent &&
          !parent.children?.filter((s) => newSelectedKeys.includes(s.id))
            ?.length
        ) {
          newSelectedKeys = newSelectedKeys.filter((i) => i !== parent.id);
        }
      }
    }

    setSelectedKeys(newSelectedKeys);
  };

  const listClasses =
    "border border-blue dark:border-border rounded-b min-w-36 py-1 h-full overflow-auto";
  const listItemClasses =
    "cursor-pointer px-2 p-1 hover:bg-slate-300 dark:hover:bg-slate-700 select-none";

  useEffect(() => {
    if (data.length > 0) setSelectAll(selectedKeys.length === data.length);
    else setSelectAll(false);
  }, [data, selectedKeys]);

  return (
    <div className="w-[48%] mb-6">
      <h4 className="font-bold mb-1.5">{title}</h4>
      <div className={listHeaderClasses}>
        <Label className={headerLabelClasses}>
          <Checkbox
            checked={selectAll}
            disabled={data.length === 0 || !access}
            onCheckedChange={(checked) => {
              setSelectedKeys(checked ? data.map((d) => d.id) : []);
              setSelectAll(!!checked);
            }}
          />
          <span className="pl-1">SELECT ALL</span>
        </Label>
      </div>
      <ul className={listClasses}>
        {constSourceData
          .filter(
            (item) =>
              data.find((d) => d.id === item.id) ||
              item.children?.find((sub) => data.find((d) => d.id === sub.id))
          )
          .map((item, ind) => {
            const checked = selectedKeys.includes(item.id);

            return (
              <li
                key={`${item}-${ind}`}
                className={listItemClasses}
              >
                <div onClick={() => handleSelect(item, !checked)}>
                  <Label className="flex items-center py-1 pointer-events-none">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(state) => handleSelect(item, state)}
                      disabled={!access}
                    />
                    <span className="pl-1">{item.name}</span>
                  </Label>
                </div>
                <ul className="ml-4">
                  {item.children
                    ?.filter((sub) => data.find((i) => i.id === item.id)?.children?.find((s) => s.id === sub.id))
                    .map((sub) => {
                      const subChecked = selectedKeys.includes(sub.id);

                      return (
                        <li
                          key={sub.id}
                          className={"py-1"}
                          onClick={() => handleSelect(sub, !subChecked, item)}
                        >
                          <Label className="flex items-center py-1 pointer-events-none">
                            <Checkbox
                              checked={subChecked}
                              onCheckedChange={(state) =>
                                handleSelect(sub, state, item)
                              }
                              disabled={!access}
                            />
                            <span className="pl-1">{sub.name}</span>
                          </Label>
                        </li>
                      );
                    })}
                </ul>
              </li>
            );
          })}
      </ul>
    </div>
  );
};

const SourceBox = ({
  access,
  title,
  data,
  items,
  selectedKeys,
  setSelectedKeys,
}: BoxProps) => {
  const [selectAll, setSelectAll] = useState(false);

  const findInItems = (items:any, id:number) => {
    return items.some((item:any) => {
      if (item.id === id) {
        return true;
      }
      // Eğer item'in children varsa, onları da kontrol et
      if (item.children) {
        return findInItems(item.children, id);
      }
      return false;
    });
  };

  const availableItems = data.filter((item) => {
    if (!item.children || item.children.length === 0) {
      // Parent-child olmayan durumda sadece parent seçilmemişse döndür
      return !items.some((t) => t.id === item.id);
    }

    // Seçilen ve seçilmeyen child'ları ayır
    const selectedChildren = item.children.filter((sub) =>
      items.some((t) => findInItems([t], sub.id))
    );
    const unselectedChildren = item.children.filter(
      (sub) => !items.some((t) => findInItems([t], sub.id))
    );

    // Eğer tüm child'lar ve parent seçilmişse, parent'i filtrele
    if (
      selectedChildren.length === item.children.length &&
      items.some((t) => t.id === item.id)
    ) {
      return false;
    }

    // Eğer bazı child'lar seçilmişse veya parent seçilmemişse parent'i göster
    if (
      selectedChildren.length > 0 || // Bazı child'lar seçilmiş
      !items.some((t) => t.id === item.id) // Parent seçilmemiş
    ) {
      // Unselected child'ları parent ile birlikte döndür
      item.children = unselectedChildren;
      return true;
    }

    return true; // Varsayılan durumda parent'i göster
  });


  const allAvailableItems = availableItems
    .map((d) => [d.id, ...(d.children ? d.children?.map((sub) => sub.id) : [])])
    .flat();

  const handleSelect = (item: TransferDataType, checked: CheckedState) => {
    const isParent = !item.parent_id;
    let newSelectedKeys = selectedKeys;

    if (checked) {
      if (isParent) {
        newSelectedKeys = [
          ...selectedKeys,
          ...(selectedKeys.includes(item.id) ? [] : [item.id]),
          ...(item.children ? item.children : [])
            .filter((s) => !selectedKeys.includes(s.id))
            .map((s) => s.id),
        ];
      } else {
        newSelectedKeys = [
          ...selectedKeys,
          ...(selectedKeys.includes(item.id) ? [] : [item.id]),
          ...(item.parent_id && !selectedKeys.includes(item.parent_id)
            ? [item.parent_id]
            : []),
        ];
      }
    } else {
      setSelectAll(false);

      if (isParent) {
        newSelectedKeys = selectedKeys.filter(
          (i) => i !== item.id && !item.children?.find((s) => i === s.id)
        );
      } else {
        newSelectedKeys = selectedKeys.filter((i) => i !== item.id);
        const parent = data.find((i) => i.id === item.parent_id);
        if (
          parent &&
          !parent.children?.filter((s) => newSelectedKeys.includes(s.id))
            ?.length
        ) {
          newSelectedKeys = newSelectedKeys.filter((i) => i !== parent.id);
        }
      }
    }

    setSelectedKeys(newSelectedKeys);
  };

  const listClasses =
    "border border-blue dark:border-border rounded-b min-w-36 py-1 h-full overflow-auto";
  const listItemClasses =
    "cursor-pointer px-2 p-1 hover:bg-slate-300 dark:hover:bg-slate-700 select-none";

  const listHeaderClasses =
    "px-2 py-1 border border-blue dark:border-border border-b-0 rounded-t bg-blue dark:bg-blue text-white";

  const headerLabelClasses = "flex items-center py-1";

  useEffect(() => {
    if (selectedKeys) {
      setSelectAll(selectedKeys.length === allAvailableItems.length);
    }
  }, [selectedKeys]);

  return (
    <div className="w-[48%] mb-6">
      <h4 className="font-bold mb-1.5">{title}</h4>
      <div className={listHeaderClasses}>
        <Label className={headerLabelClasses}>
          <Checkbox
            checked={selectAll}
            disabled={data.length === 0 || !access}
            onCheckedChange={(checked) => {
              setSelectedKeys(
                checked
                  ? availableItems
                      .map((d) => [
                        d.id,
                        ...(d.children ? d.children?.map((sub) => sub.id) : []),
                      ])
                      .flat()
                  : []
              );
              setSelectAll(!!checked);
            }}
          />
          <span className="pl-1">SELECT ALL</span>
        </Label>
      </div>
      <ul className={listClasses}>
        {availableItems.map((item, ind) => {
          const checked = selectedKeys.includes(item.id);

          return (
            <li key={`${item}-${ind}`} className={clsx(listItemClasses, {})}>
              <div onClick={() => handleSelect(item, !checked)}>
                <Label className="flex items-center py-1 pointer-events-none">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(state) => handleSelect(item, state)}
                    disabled={!access}
                  />
                  <span className="pl-1">{item.name}</span>
                </Label>
              </div>
              <ul className="ml-4">
                {item.children
                  ?.filter((sub) => !items.find((i) => i.id === sub.id))
                  .map((sub) => {
                    const subChecked = selectedKeys.includes(sub.id);

                    return (
                      <li
                        key={sub.id}
                        className={"py-1"}
                        onClick={() => handleSelect(sub, !subChecked)}
                      >
                        <Label className="flex items-center py-1 pointer-events-none">
                          <Checkbox
                            checked={subChecked}
                            onCheckedChange={(state) =>
                              handleSelect(sub, state)
                            }
                            disabled={!access}
                          />
                          <span className="pl-1">{sub.name}</span>
                        </Label>
                      </li>
                    );
                  })}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export type TransferDataType = {
  id: number;
  name: string;
  status?: boolean;
  children?: TransferDataType[];
  order: number;
  parent_id?: number;
};

type Props = {
  access?: boolean;
  titles: [string, string];
  targetData: TransferDataType[];
  sourceData: TransferDataType[];
  constSourceData: TransferDataType[];
  onSelectedChange: (targets: TransferDataType[]) => void;
};

const TransferList = ({
  access = true,
  titles,
  targetData,
  sourceData,
  constSourceData,
  onSelectedChange,
}: Props) => {
  const sources = sourceData.map((source) => ({
    ...source,
    children: (source.children || []).map((sub) => ({
      ...sub,
      parent_id: source.id,
    })),
  }));

  const [selectedSources, setSelectedSources] = useState<number[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<number[]>([]);

  const handleTransfer = (sourcesToTargets: boolean = true) => {
    let newTargets: TransferDataType[] = [];
    let newSources: TransferDataType[] = [];

    if (sourcesToTargets) {
      newTargets = [...targetData];
  
      sourceData
        .filter(
          (i) =>
            selectedSources.includes(i.id) ||
            i.children?.some((s) => selectedSources.includes(s.id))
        )
        .forEach((i) => {
          const selectedChildren = (i.children || []).filter((s) =>
            selectedSources.includes(s.id)
          );
  
          const existingParent = newTargets.find((t) => t.id === i.id);
  
          if (existingParent) {
            existingParent.children = [
              ...(existingParent.children || []),
              ...selectedChildren.filter(
                (s) =>
                  !(existingParent.children || []).some((child) => child.id === s.id)
              ),
            ];
          } else {
            newTargets.push({
              id: i.id,
              name: i.name,
              order: newTargets.length + 1,
              ...(selectedChildren.length > 0 && { children: selectedChildren }),
            });
          }
        });
    } else {
      newTargets = targetData.filter((i) => !selectedTargets.includes(i.id));
    }
  
    setSelectedSources([]);
    onSelectedChange(newTargets);
  };
  

  return (
    <div className="transfer-list flex gap-4 h-64 w-full mb-12">
      <SourceBox
        access={access}
        title={titles[0]}
        data={sources}
        items={targetData}
        constSourceData={constSourceData}
        selectedKeys={selectedSources}
        setSelectedKeys={setSelectedSources}
      />
      <div className="flex w-[4%] justify-center flex-col gap-2">
        <Button
          onClick={() => handleTransfer(false)}
          disabled={!access || !selectedTargets.length}
          size="icon"
          className="w-full h-6 bg-blue text-white"
        >
          <ArrowLeft />
        </Button>
        <Button
          onClick={() => handleTransfer()}
          disabled={!access || !selectedSources.length}
          size="icon"
          className="w-full h-6 bg-blue text-white"
        >
          <ArrowRight />
        </Button>
      </div>
      <TargetBox
        access={access}
        title={titles[1]}
        data={targetData}
        items={sources}
        constSourceData={constSourceData}
        selectedKeys={selectedTargets}
        setSelectedKeys={setSelectedTargets}
      />
    </div>
  );
};

export default TransferList;
